// IQ Test — pure, confirmed scoring calculations. No React, no side
// effects, no reads from reportData; report-model.js and the dashboard
// overview (assessment-overview/component.js) both call these same
// functions — never a second/duplicated implementation.
//
// Contract (2026-09-17): the backend's `trait.score` is now the
// authoritative raw correct-answer count for that section (an integer,
// e.g. 11) — it is read directly, never reconstructed from
// `trait.percentage`. `trait.percentage` remains the independent,
// authoritative source for percentage display and band classification
// (deriveBandKeyFromPercentage) — the two are never derived from each
// other. Old reports persisted under the previous decimal-score format are
// not supported here (team-confirmed: no compatibility shim needed).

import { TOTAL_QUESTIONS, OFFICIAL_PERFORMANCE_BANDS } from "./iq-assessment-config";

// Sums the four already-authoritative section raw scores. Returns null if
// any section score is missing — never a partial/guessed sum.
export function deriveOverallRawScore(domainRawScores) {
  if (!domainRawScores.length || domainRawScores.some((s) => s == null)) return null;
  return domainRawScores.reduce((sum, s) => sum + Number(s), 0);
}

// Rounded to 2 decimals (matches the backend's own percentage precision,
// e.g. 46.67) — not to a whole number; display code rounds further.
export function deriveOverallPercentage(rawScore, maxRawScore = TOTAL_QUESTIONS) {
  if (rawScore == null || maxRawScore == null) return null;
  return Math.round((rawScore / maxRawScore) * 10000) / 100;
}

// Rounds to the nearest whole percent before matching
// OFFICIAL_PERFORMANCE_BANDS's integer boundaries. Applies identically to
// the overall percentage and to each section's own percentage.
export function deriveBandKeyFromPercentage(percentage) {
  if (percentage == null) return null;
  const rounded = Math.round(percentage);
  const band = OFFICIAL_PERFORMANCE_BANDS.find((b) => rounded >= b.min && rounded <= b.max);
  return band ? band.bandKey : null;
}

// Consistency (product-confirmed): clamp(100 - (max - min) of the four
// section percentages, 0, 100) — how uniform performance is across
// sections, independent of the IQ model. Uses the actual (possibly
// fractional) percentages; only the final result is rounded, by the
// caller. Returns null if any section percentage is missing.
export function deriveConsistency(sectionPercentages) {
  if (!sectionPercentages.length || sectionPercentages.some((p) => p == null)) return null;
  const spread = Math.max(...sectionPercentages) - Math.min(...sectionPercentages);
  return Math.min(100, Math.max(0, 100 - spread));
}
